import React, { useState, useEffect } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import './GoogleReviews.css';

const LOCATIONS = [
    'Crown Cleaners EP True West Des Moines',
    'Crown Cleaners Greenway West Des Moines',
    'Crown Cleaners 42nd Street Des Moines',
    'Crown Cleaners Park Avenue Des Moines',
    'Crown Cleaners SW 9th Des Moines',
    'Crown Cleaners Grimes',
    'Crown Cleaners Ankeny',
    'Crown Cleaners Boone'
];

function GoogleReviews() {
    const [reviews, setReviews] = useState([]);
    const [currentSet, setCurrentSet] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const formatName = (authorName) => {
        if (!authorName) return '';
        
        const nameParts = authorName.trim().split(' ');
        if (nameParts.length === 1) return nameParts[0];
        
        const firstName = nameParts[0];
        const lastInitial = nameParts[nameParts.length - 1][0];
        return `${firstName} ${lastInitial}.`;
    };

    useEffect(() => {
        const loader = new Loader({
            apiKey: 'AIzaSyCwGgap_Ut-JrGG39Uz0XAlNTiVRfdTozI',
            version: 'weekly',
            libraries: ['places']
        });

        const fetchAllReviews = async () => {
            try {
                await loader.load();

                const mapDiv = document.createElement('div');
                mapDiv.style.display = 'none';
                document.body.appendChild(mapDiv);

                const map = new window.google.maps.Map(mapDiv, {
                    center: { lat: 41.5868, lng: -93.7125 },
                    zoom: 13
                });

                const service = new window.google.maps.places.PlacesService(map);

                const findPlace = (locationName) => {
                    return new Promise((resolve, reject) => {
                        service.findPlaceFromQuery({
                            query: locationName,
                            fields: ['place_id', 'name']
                        }, (results, status) => {
                            if (status === 'OK' && results && results.length > 0) {
                                resolve(results[0]);
                            } else {
                                resolve(null);
                            }
                        });
                    });
                };

                const getPlaceDetails = (placeId) => {
                    return new Promise((resolve, reject) => {
                        service.getDetails({
                            placeId: placeId,
                            fields: ['name', 'rating', 'reviews']
                        }, (details, status) => {
                            if (status === 'OK' && details) {
                                resolve(details);
                            } else {
                                resolve(null);
                            }
                        });
                    });
                };

                // Fetch reviews from all locations
                const allReviews = [];
                for (const locationName of LOCATIONS) {
                    const place = await findPlace(locationName);
                    if (place) {
                        const details = await getPlaceDetails(place.place_id);
                        if (details && details.reviews) {
                            const locationReviews = details.reviews
                                .filter(review => review.rating >= 4)
                                .map(review => ({
                                    ...review,
                                    locationName: details.name
                                }));
                            allReviews.push(...locationReviews);
                        }
                    }
                }

                if (allReviews.length > 0) {
                    // Shuffle the reviews and take the top 9
                    const shuffledReviews = allReviews
                        .sort(() => 0.5 - Math.random())
                        .slice(0, 9);

                    setReviews(shuffledReviews);
                } else {
                    throw new Error('No reviews found');
                }

                setLoading(false);
                document.body.removeChild(mapDiv);

            } catch (err) {
                console.error('Error:', err);
                setError(err.message || 'Failed to load reviews');
                setLoading(false);
            }
        };

        fetchAllReviews();
    }, []);

    useEffect(() => {
        if (reviews.length > 0) {
            const timer = setInterval(() => {
                setCurrentSet((prevSet) => (prevSet + 1) % 3);
            }, 5000);

            return () => clearInterval(timer);
        }
    }, [reviews.length]);

    if (loading) return <div className="review-section"><h2>Loading reviews...</h2></div>;
    if (error) return <div className="review-section"><h2>Error: {error}</h2></div>;
    if (reviews.length === 0) return <div className="review-section"><h2>No reviews available</h2></div>;

    return (
        <section className="review-section">
            <h2>What Our Customers Say</h2>
            <div className="review-slider">
                <div className={`review-container slide-position-${currentSet}`}>
                    {reviews.map((review, index) => (
                        <div key={index} className="review-card">
                            <div className="stars">
                                {"★".repeat(review.rating)}
                            </div>
                            <p className="review-text">{review.text}</p>
                            <div className="review-author">
                                <span className="name">{formatName(review.author_name)}</span>
                                <span className="date">{review.relative_time_description}</span>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="slider-dots">
                    {reviews.map((_, index) => (
                        <button
                            key={index}
                            className={`dot ${currentSet === index ? 'active' : ''}`}
                            onClick={() => setCurrentSet(index)}
                            aria-label={`View review ${index + 1} of ${reviews.length}`}
                            aria-current={currentSet === index ? 'true' : 'false'}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

export default GoogleReviews; 